import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Divider, Dropdown, Menu, Tree} from 'antd';
import {
	CloseOutlined,
	DeleteOutlined,
	EditOutlined,
	MenuOutlined,
	PlusCircleOutlined,
	PlusOutlined,
	RightOutlined,
	SettingFilled,
	ProfileOutlined,
} from '@ant-design/icons';
import {getViewLabel} from '@/utils/diagram/getViewLabel';
import type {
	DiagramNavigatorConceptDataNode,
	DiagramNavigatorTreeProps,
} from '@/types/components/diagramNavigatorTree.types';
import './DiagramNavigatorTree.css';
import {DataNode} from 'antd/es/tree';
import DiagramCatalogBrowser from '@components/diagram/DiagramCatalogBrowser';
import TruncatedText from '@/components/TruncatedText';
const {DirectoryTree} = Tree;

/** Папки ArchiMate (порядок как в Archi) с иконками */
const TOOLBAR_DIVIDER_STYLE: React.CSSProperties = {
	margin: '0 6px',
	height: 22,
	top: 0,
	borderColor: 'rgba(0,0,0,0.06)',
};

const FOLDER_KEYS = [
	{key: 'strategy', title: 'Strategy'},
	{key: 'business', title: 'Business'},
	{key: 'application', title: 'Application'},
	{key: 'technology-physical', title: 'Technology & Physical'},
	{key: 'motivation', title: 'Motivation'},
	{key: 'implementation-migration', title: 'Implementation & Migration'},
	{key: 'other', title: 'Other'},
	{key: 'relations', title: 'Relations'},
	{key: 'views', title: 'Views'},
] as const;

const DiagramNavigatorTree: React.FC<DiagramNavigatorTreeProps> = ({
	diagramName,
	views,
	activeViewId,
	modelContent,
	onViewChange,
	onAddView,
	onEditView,
	onDeleteView,
	onSettingsClick,
	onInfoClick,
	assistantSlot,
	onClose,
	onNavigateToConcept,
	onAddConceptToView,
	onDeleteConceptFromView,
	onDeleteConceptFromModel,
	onOpenConceptProperties,
	isConceptOnCurrentView,
	isConceptOnAnyView,
	canDelete = false,
	catalog,
}) => {
	const [treeExpanded, setTreeExpanded] = useState(true);
	const [contextMenu, setContextMenu] = useState<
		| {type: 'concept'; x: number; y: number; conceptId: string; isRelationship: boolean}
		| {type: 'viewFolder'; x: number; y: number}
		| {type: 'viewItem'; x: number; y: number; viewId: string}
		| null
	>(null);
	const closeContextMenu = useCallback(() => setContextMenu(null), []);

	useEffect(() => {
		if (!contextMenu) return;
		const onDocClick = () => closeContextMenu();
		const t = setTimeout(() => {
			document.addEventListener('click', onDocClick);
			document.addEventListener('contextmenu', onDocClick);
		}, 0);
		return () => {
			clearTimeout(t);
			document.removeEventListener('click', onDocClick);
			document.removeEventListener('contextmenu', onDocClick);
		};
	}, [contextMenu, closeContextMenu]);

	const treeData = useMemo<DiagramNavigatorConceptDataNode[]>(() => {
		const viewNodes: DiagramNavigatorConceptDataNode[] = views.map(v => {
			const vid = v.id ?? v.$attrs?.id ?? '';
			const label = getViewLabel(v);
			return {
				key: `view-${vid}`,
				title: <TruncatedText text={label} />,
				selectable: true,
				isLeaf: true,
			};
		});

		const elementsByFolder = modelContent?.elementsByFolder ?? {};

		const folderNodes: DiagramNavigatorConceptDataNode[] = FOLDER_KEYS.map(({key, title}) => {
			let children: DiagramNavigatorConceptDataNode[];

			if (key === 'views') {
				children = viewNodes;
			} else {
				const items = elementsByFolder[key] ?? [];
				const isRelationship = key === 'relations';
				children = items.map(item => {
					const conceptId = item.id ?? '';
					const label = item.name || item.type;
					const onAnyView = isConceptOnAnyView?.(conceptId, isRelationship) ?? true;
					return {
						key: `concept-${key}-${item.id}`,
						title: (
							<span
								style={{
									display: 'inline-block',
									maxWidth: '100%',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									color: onAnyView ? undefined : '#8c8c8c',
									fontStyle: onAnyView ? undefined : 'italic',
								}}
								title={
									onAnyView
										? 'Правый клик — Перейти к элементу'
										: 'Не используется ни на одной диаграмме. Правый клик — Добавить на диаграмму.'
								}
							>
								{label}
							</span>
						),
						selectable: false,
						isLeaf: true,
						conceptId: conceptId || undefined,
						isRelationship,
					};
				});
			}

			const count = key === 'views' ? views.length : (elementsByFolder[key]?.length ?? 0);
			return {
				key: `folder-${key}`,
				title: count > 0 ? `${title} (${count})` : title,
				selectable: false,
				isLeaf: false,
				children,
			};
		});

		return folderNodes;
	}, [views, modelContent, isConceptOnAnyView]);

	const selectedKeys = useMemo(() => {
		if (!activeViewId) return [];
		return [`view-${activeViewId}`];
	}, [activeViewId]);

	const handleSelect = (_: React.Key[], info: {node: DataNode}) => {
		const key = info.node.key as string;
		if (key.startsWith('view-')) {
			const viewId = key.replace(/^view-/, '');
			if (viewId) onViewChange(viewId);
		}
	};

	const handleRightClick = useCallback(
		(info: {event: React.MouseEvent; node: DiagramNavigatorConceptDataNode}) => {
			const {event, node} = info;
			const key = (node.key as string) ?? '';
			const conceptNode = node as DiagramNavigatorConceptDataNode;
			if (key === 'folder-views' && onAddView) {
				event.preventDefault();
				setContextMenu({type: 'viewFolder', x: event.clientX, y: event.clientY});
			} else if (key.startsWith('view-') && (onEditView || onDeleteView || onSettingsClick)) {
				event.preventDefault();
				const viewId = key.replace(/^view-/, '');
				setContextMenu({type: 'viewItem', x: event.clientX, y: event.clientY, viewId});
			} else if (conceptNode.conceptId && onNavigateToConcept) {
				event.preventDefault();
				setContextMenu({
					type: 'concept',
					x: event.clientX,
					y: event.clientY,
					conceptId: conceptNode.conceptId,
					isRelationship: conceptNode.isRelationship ?? false,
				});
			}
		},
		[onNavigateToConcept, onAddView, onEditView, onDeleteView, onSettingsClick]
	);

	return (
		<div
			style={{
				position: 'absolute',
				left: 16,
				top: 16,
				zIndex: 10,
				display: 'flex',
				flexDirection: 'column',
				gap: 8,
				alignItems: 'flex-start',
			}}
		>
			{/* Компактный островок с кнопками — фиксированный размер */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					flexWrap: 'nowrap',
					gap: 0,
					padding: '6px',
					background: '#fff',
					border: '1px solid #d9d9d9',
					borderRadius: 12,
					boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
					minWidth: 0,
				}}
			>
				<Button
					type="text"
					icon={<MenuOutlined />}
					onClick={() => setTreeExpanded(v => !v)}
					title={treeExpanded ? 'Скрыть дерево' : 'Показать дерево'}
					style={{flexShrink: 0}}
				/>
				<Divider orientation="vertical" style={TOOLBAR_DIVIDER_STYLE} />
				<TruncatedText
					text={diagramName || 'Модель'}
					strong
					onClick={onInfoClick}
					style={{
						maxWidth: 180,
						cursor: onInfoClick ? 'pointer' : 'default',
					}}
				/>
				{assistantSlot ? (
					<>
						<Divider orientation="vertical" style={TOOLBAR_DIVIDER_STYLE} />
						<span style={{display: 'flex', alignItems: 'center', flexShrink: 0}}>
							{assistantSlot}
						</span>
					</>
				) : null}
				{onClose && (
					<>
						<Divider orientation="vertical" style={TOOLBAR_DIVIDER_STYLE} />
						<Button
							type="text"
							icon={<CloseOutlined />}
							onClick={onClose}
							title="Закрыть"
							style={{flexShrink: 0}}
						/>
					</>
				)}
			</div>

			{treeExpanded && (
				<div
					style={{
						padding: 0,
						background: '#fff',
						border: '1px solid #d9d9d9',
						borderRadius: 8,
						boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
						maxHeight: 420,
						overflowY: 'auto',
						minWidth: 220,
						maxWidth: 320,
					}}
				>
					<DirectoryTree
						styles={{root: {borderRadius: 0, padding: 5}}}
						multiple
						showLine
						blockNode
						defaultExpandedKeys={['folder-views']}
						treeData={treeData}
						selectedKeys={selectedKeys}
						onSelect={handleSelect}
						onRightClick={handleRightClick}
						style={{fontSize: 13}}
						className="diagram-navigator-tree"
					/>
					{catalog && onAddConceptToView && (
						<DiagramCatalogBrowser
							projectId={catalog.projectId}
							diagramId={catalog.diagramId}
							onPlace={(identifier, hint) => onAddConceptToView(identifier, false, hint)}
							onNavigate={id => onNavigateToConcept?.(id, false)}
						/>
					)}
				</div>
			)}
			{contextMenu && (
				<Dropdown
					open
					styles={{root: {zIndex: 1050}}}
					onOpenChange={open => !open && closeContextMenu()}
					popupRender={() => (
						<Menu
							items={
								contextMenu.type === 'viewFolder'
									? [
											...(onAddView
												? [
														{
															key: 'add',
															icon: <PlusOutlined />,
															label: 'Добавить диаграмму',
															onClick: () => {
																onAddView();
																closeContextMenu();
															},
														},
													]
												: []),
										]
									: contextMenu.type === 'viewItem'
										? [
												...(onEditView
													? [
															{
																key: 'edit',
																icon: <EditOutlined />,
																label: 'Изменить название',
																onClick: () => {
																	onEditView(contextMenu.viewId);
																	closeContextMenu();
																},
															},
														]
													: []),
												...(onDeleteView
													? [
															{
																key: 'delete',
																icon: <DeleteOutlined />,
																label: 'Удалить',
																danger: true,
																disabled: !canDelete,
																onClick: () => {
																	onDeleteView(
																		contextMenu.viewId
																	);
																	closeContextMenu();
																},
															},
														]
													: []),
												...(onSettingsClick
													? [
															{
																key: 'settings',
																icon: <SettingFilled />,
																label: 'Настройки',
																onClick: () => {
																	onSettingsClick();
																	closeContextMenu();
																},
															},
														]
													: []),
											]
										: [
												{
													key: 'navigate',
													icon: <RightOutlined />,
													label: 'Перейти',
													onClick: () => {
														onNavigateToConcept?.(
															contextMenu.conceptId,
															contextMenu.isRelationship
														);
														closeContextMenu();
													},
												},
												...(onOpenConceptProperties
													? [
															{
																key: 'properties',
																icon: <ProfileOutlined />,
																label: 'Свойства',
																onClick: () => {
																	onOpenConceptProperties(
																		contextMenu.conceptId,
																		contextMenu.isRelationship
																	);
																	closeContextMenu();
																},
															},
														]
													: []),
												...(onAddConceptToView &&
												(!isConceptOnCurrentView ||
													!isConceptOnCurrentView(
														contextMenu.conceptId,
														contextMenu.isRelationship
													))
													? [
															{
																key: 'addToView',
																icon: <PlusCircleOutlined />,
																label: 'Добавить на диаграмму',
																onClick: () => {
																	onAddConceptToView(
																		contextMenu.conceptId,
																		contextMenu.isRelationship
																	);
																	closeContextMenu();
																},
															},
														]
													: []),
												...(onDeleteConceptFromView &&
												(isConceptOnCurrentView?.(
													contextMenu.conceptId,
													contextMenu.isRelationship
												) ??
													true)
													? [
															{
																key: 'deleteFromView',
																icon: <DeleteOutlined />,
																label: 'Удалить с диаграммы',
																danger: true,
																onClick: () => {
																	onDeleteConceptFromView(
																		contextMenu.conceptId,
																		contextMenu.isRelationship
																	);
																	closeContextMenu();
																},
															},
														]
													: []),
												...(onDeleteConceptFromModel
													? [
															{
																key: 'deleteFromModel',
																icon: <DeleteOutlined />,
																label: 'Удалить из модели',
																danger: true,
																onClick: () => {
																	onDeleteConceptFromModel(
																		contextMenu.conceptId,
																		contextMenu.isRelationship
																	);
																	closeContextMenu();
																},
															},
														]
													: []),
											]
							}
							style={{border: 'none', minWidth: 180}}
						/>
					)}
				>
					<div
						style={{
							position: 'fixed',
							left: contextMenu.x,
							top: contextMenu.y,
							width: 1,
							height: 1,
							pointerEvents: 'none',
							zIndex: -1,
						}}
					/>
				</Dropdown>
			)}
		</div>
	);
};

export default DiagramNavigatorTree;
